import { useCallback, useEffect, useState } from 'react';
import { Key, RefreshCw, Copy, Check, AlertTriangle, CreditCard, FileSignature, Users } from 'lucide-react';
import { apiClient as api } from '../../lib/api';

type PaymentTerms = 'immediate' | 'net7' | 'net15' | 'net30';
// What an onboarding is waiting on, in the words staff act on.
const NEXT_STEP: Record<string, string> = {
  physician_registration: 'Waiting for the physician to open the link and register or sign in',
  bloom_verification: 'Waiting on Bloom: identity and credential verification (or network assignment and agreement)',
  bloom_contact_review: 'Waiting on Bloom: confirm the physician’s direct contact details',
  physician_acceptance: 'Waiting for the physician to accept the connection on the link',
  connected: 'Connected',
};
const TERMS: Array<{ value: PaymentTerms; label: string }> = [
  { value: 'immediate', label: 'Card per order' }, { value: 'net7', label: 'Net 7' }, { value: 'net15', label: 'Net 15' }, { value: 'net30', label: 'Net 30' },
];

interface PartnerApiSectionProps {
  /** The supervisor account this credential belongs to. */
  supervisorUserId: string;
  /** Only admins may mint, rotate, enable, or suspend a credential. */
  isAdmin: boolean;
}

/**
 * Integration API access for a supervisor account.
 *
 * A partner is a supervisor — it owns catalogs, sits in the pricing hierarchy,
 * and is paid like any other supervisor. The only thing that makes it a partner
 * is the machine credential managed here, so this lives alongside the rest of
 * the supervisor's details rather than in a separate partner screen.
 *
 * The key itself is shown exactly once, on creation or rotation: only its
 * SHA-256 hash is stored, so it can never be read back.
 */
export function PartnerApiSection({ supervisorUserId, isAdmin }: PartnerApiSectionProps) {
  const [partner, setPartner] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [networks, setNetworks] = useState<Array<{ _id: string; name: string; isActive?: boolean }>>([]);
  const [terms, setTerms] = useState<PaymentTerms>('immediate');
  const [creditLimit, setCreditLimit] = useState<string>('');
  const [contractNetwork, setContractNetwork] = useState<string>('');
  const [contractVersion, setContractVersion] = useState<string>('');
  const [onboardings, setOnboardings] = useState<any[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await api.getApiPartners();
    if (result.success) {
      const found = (result.data || []).find((p: any) => p.id === supervisorUserId) || null;
      setPartner(found);
      if (found) {
        if (isAdmin) {
          const flows = await api.getApiPartnerOnboardings(found.id);
          setOnboardings(flows.success ? flows.data || [] : []);
        }
        setTerms(found.billing?.arEnabled ? found.billing.paymentTerms : 'immediate');
        setCreditLimit(found.billing?.creditLimit == null ? '' : String(found.billing.creditLimit));
        setContractNetwork(found.networkAgreement?.telemedNetworkId || '');
        setContractVersion(found.networkAgreement?.version || '');
      }
    } else {
      setError(result.error || 'Failed to load integration access');
    }
    setLoading(false);
  }, [supervisorUserId, isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isAdmin) return;
    api.getTelemedNetworks().then(result => { if (result.success) setNetworks((result.data as any[]) || []); });
  }, [isAdmin]);

  const saveTerms = () => run(() => api.updateApiPartner(partner.id, { billing: {
    arEnabled: terms !== 'immediate', paymentTerms: terms, creditLimit: creditLimit.trim() === '' ? null : Number(creditLimit),
  } }));
  const saveContract = () => run(() => api.updateApiPartner(partner.id, {
    networkAgreement: contractNetwork ? { telemedNetworkId: contractNetwork, ...(contractVersion.trim() && { version: contractVersion.trim() }) } : null,
  }));

  async function run(action: () => Promise<any>, onKey?: (key: string) => void) {
    setBusy(true);
    setError(null);
    const result = await action();
    if (result.success) {
      if (onKey && result.data?.apiKey) onKey(result.data.apiKey);
      await load();
    } else {
      setError(result.error || 'That did not work');
    }
    setBusy(false);
  }

  async function copyKey() {
    if (!freshKey) return;
    try {
      await navigator.clipboard.writeText(freshKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select the key and copy it manually');
    }
  }

  if (loading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        Loading integration access...
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Key className="h-4 w-4 text-gray-600" />
          <h4 className="text-sm font-medium text-gray-900">Integration API</h4>
        </div>
        {partner && (
          <div className="flex items-center space-x-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              partner.apiEnabled && partner.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
            }`}>
              {partner.status !== 'active' ? partner.status : partner.apiEnabled ? 'enabled' : 'disabled'}
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-start space-x-2 mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {freshKey && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded p-3">
          <div className="text-xs font-medium text-amber-900 mb-2">
            Copy this key now — it is never shown again.
          </div>
          <div className="flex items-center space-x-2">
            <code className="flex-1 text-xs bg-white border rounded px-2 py-1.5 break-all font-mono">{freshKey}</code>
            <button
              onClick={copyKey}
              className="flex items-center space-x-1 px-2 py-1.5 text-xs text-amber-800 hover:bg-amber-100 rounded border border-amber-300"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              <span>{copied ? 'Copied' : 'Copy'}</span>
            </button>
          </div>
        </div>
      )}

      {!partner ? (
        <div className="space-y-3">
          <div className="text-sm text-gray-600">
            API access is off for this supervisor. Turning it on issues a key that lets a partner
            system read its connected clinics and place orders against the catalogs assigned to them.
            <span className="block mt-1 text-gray-500">
              It also gives the partner an <strong>API Tokens</strong> page in their own
              account, where they can create, rotate and revoke their own tokens.
            </span>
          </div>
          {isAdmin && (
            <button
              onClick={() => run(() => api.enableApiPartner(supervisorUserId), setFreshKey)}
              disabled={busy}
              className="flex items-center space-x-1 px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
            >
              <Key className="h-3 w-3" />
              <span>{busy ? 'Enabling...' : 'Enable API access'}</span>
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-gray-700">
            <span className="text-gray-500">Integration name:</span> {partner.name}
          </div>
          {!partner.onboardingDistributorId && (
            <div className="text-xs text-gray-600 italic">
              New clinics will be placed directly under this supervisor unless an onboarding Account Manager is assigned.
            </div>
          )}
          {isAdmin && (
            <div className="grid gap-3 sm:grid-cols-2">
              {/* Payment terms: same meaning as a clinic's AR settings. On terms,
                  API orders release against an open invoice within the credit
                  limit instead of an immediate card charge. Orders are always
                  billed at the partner's own cost, with no commission. */}
              <div className="bg-white border border-gray-200 rounded p-3 space-y-2">
                <div className="flex items-center space-x-1 text-xs font-medium text-gray-800">
                  <CreditCard className="h-3.5 w-3.5" /><span>Payment terms</span>
                </div>
                <select value={terms} onChange={e => setTerms(e.target.value as PaymentTerms)}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5">
                  {TERMS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input type="number" min={0} step="0.01" value={creditLimit} onChange={e => setCreditLimit(e.target.value)}
                  disabled={terms === 'immediate'} placeholder="Credit limit (USD, blank = unlimited)"
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 disabled:bg-gray-50" />
                <button onClick={saveTerms} disabled={busy}
                  className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50">
                  Save terms
                </button>
              </div>
              {/* The partner's signed doctor-network contract. With it on file
                  the partner holds the risk for the network clinics it onboards:
                  they skip per-clinic agreement, ID proofing and contact review
                  and are placed on this network at acceptance. */}
              <div className="bg-white border border-gray-200 rounded p-3 space-y-2">
                <div className="flex items-center space-x-1 text-xs font-medium text-gray-800">
                  <FileSignature className="h-3.5 w-3.5" /><span>Doctor-network contract</span>
                  {partner.networkAgreement && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">signed</span>}
                </div>
                <select value={contractNetwork} onChange={e => setContractNetwork(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5">
                  <option value="">No contract on file</option>
                  {networks.filter(n => n.isActive !== false).map(n => <option key={n._id} value={n._id}>{n.name}</option>)}
                </select>
                <input value={contractVersion} onChange={e => setContractVersion(e.target.value)} disabled={!contractNetwork}
                  placeholder="Contract version or reference" maxLength={100}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 disabled:bg-gray-50" />
                <button onClick={saveContract} disabled={busy}
                  className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50">
                  {contractNetwork ? 'Record contract' : 'Withdraw contract'}
                </button>
              </div>
            </div>
          )}
          {isAdmin && onboardings.length > 0 && (
            /* Clinics this partner has invited, and what each is waiting on. A
               physician who registered through the link is invisible to the
               partner's API until staff confirm their direct contact details
               here; that step used to have no screen at all. */
            <div className="bg-white border border-gray-200 rounded p-3 space-y-2">
              <div className="flex items-center space-x-1 text-xs font-medium text-gray-800">
                <Users className="h-3.5 w-3.5" /><span>Clinic onboarding</span>
              </div>
              <ul className="divide-y divide-gray-100">
                {onboardings.map(flow => (
                  <li key={flow.id} className="py-2 text-xs">
                    <div className="flex flex-wrap items-center gap-x-2">
                      <span className="font-medium text-gray-900">{flow.physician?.name || flow.email}</span>
                      <span className="text-gray-500">{flow.prescribingMode === 'doctor_network' ? 'doctor network' : 'own physician'}</span>
                      <span className={`px-1.5 py-0.5 rounded-full ${flow.progress.connected ? 'bg-green-100 text-green-700' : flow.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-600'}`}>
                        {flow.progress.connected ? 'connected' : flow.status}
                      </span>
                    </div>
                    <div className="text-gray-600 mt-0.5">{NEXT_STEP[flow.progress.nextStep] || (flow.status === 'expired' ? 'Link expired' : 'Link revoked')}</div>
                    {flow.progress.nextStep === 'bloom_contact_review' && flow.physician && (
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="text-gray-700">{flow.physician.email} · {flow.physician.phone}</span>
                        <button onClick={() => run(() => api.reviewApiPartnerPhysicianContact(partner.id, flow.physician.id))} disabled={busy}
                          className="px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                          title="Confirms these are the physician's direct email and phone, and invites them to accept the connection">
                          Confirm contact details
                        </button>
                      </div>
                    )}
                    {flow.progress.nextStep === 'bloom_verification' && flow.physician && (
                      <div className="mt-0.5 text-gray-500">ID proofing: {flow.physician.idProofingStatus || 'not started'} · credentials: {flow.physician.credentialStatus || 'not verified'}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => run(() => api.updateApiPartner(partner.id, { apiEnabled: !partner.apiEnabled }))}
                disabled={busy}
                className="px-3 py-1.5 text-xs text-gray-700 bg-white hover:bg-gray-100 rounded border border-gray-300 disabled:opacity-50"
              >
                {partner.apiEnabled ? 'Disable API access' : 'Re-enable API access'}
              </button>
              <button
                onClick={() => run(() => api.updateApiPartner(partner.id, {
                  status: partner.status === 'active' ? 'suspended' : 'active'
                }))}
                disabled={busy}
                className="px-3 py-1.5 text-xs text-gray-700 bg-white hover:bg-gray-100 rounded border border-gray-300 disabled:opacity-50"
              >
                {partner.status === 'active' ? 'Suspend' : 'Reactivate'}
              </button>
              <button
                onClick={() => run(() => api.rotateApiPartnerKey(partner.id), setFreshKey)}
                disabled={busy}
                className="flex items-center space-x-1 px-3 py-1.5 text-xs text-gray-700 bg-white hover:bg-gray-100 rounded border border-gray-300 disabled:opacity-50"
                title="Issues a new key and immediately stops the old one working"
              >
                <RefreshCw className="h-3 w-3" />
                <span>Rotate key</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
